/**
 * Profile picture upload and display using content-management dedicated endpoints:
 * - POST /api/v1/contents/profile-picture (upload, with JWT)
 * - GET /api/v1/contents/profile-picture/{id} (display, public)
 * Then PATCH user-profile with profilePictureId.
 */
import { contentApi } from '@/services/adminApi';
import { profileApi } from '@/services/profileApi';

const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export interface UploadProfilePictureResult {
  profilePictureId: string;
  fileUrl?: string;
}

export function validateProfilePictureFile(file: File): void {
  if (!file.type || !ALLOWED_TYPES.includes(file.type)) {
    throw new Error(`Please choose a JPEG, PNG, WebP, or GIF image. Got: ${file.type || 'unknown'}`);
  }
  if (file.size > MAX_SIZE_BYTES) {
    throw new Error(`Image must be under ${MAX_SIZE_BYTES / 1024 / 1024} MB.`);
  }
}

/** Map API error status to user-friendly message when server message is missing. */
export function getProfilePictureUploadErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  const status = typeof err === 'object' && err !== null && 'status' in err ? (err as { status?: number }).status : undefined;
  switch (status) {
    case 400:
      return 'Invalid image. Use JPEG, PNG, WebP or GIF under 5 MB.';
    case 401:
      return 'Please sign in again to upload a photo.';
    case 413:
      return 'File is too large. Maximum size is 5 MB.';
    case 429:
      return 'Too many uploads. Please try again in a minute.';
    case 503:
      return 'Upload service is temporarily unavailable. Please try again in a few minutes.';
    default:
      return 'Upload failed. Please try again.';
  }
}

/**
 * Upload profile picture via dedicated endpoint, then set profile.profilePictureId.
 * Uses POST /api/v1/contents/profile-picture (multipart, JWT); then PATCH user-profile.
 */
export async function uploadProfilePicture(userId: string, file: File): Promise<UploadProfilePictureResult> {
  validateProfilePictureFile(file);

  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', 'Profile Picture');

  const content = await contentApi.uploadProfilePicture(formData);
  const contentId = content.id ?? (content as { id?: string }).id;
  if (!contentId) {
    throw new Error('Content service did not return an ID for the uploaded image.');
  }

  await profileApi.updateProfilePicture(userId, contentId);

  return {
    profilePictureId: contentId,
    fileUrl: content.fileUrl,
  };
}
